import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { getExecTeams, type ExecTeamSection } from '../api/exec';
import { getMyHistory, type Assessment } from '../api/assessment';
import { useAuth } from '../store/AuthContext';
import { exportToCsv, todayIso } from '../utils/exportCsv';

export function ExecDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [teams, setTeams] = useState<ExecTeamSection[] | null>(null);
  const [mine, setMine] = useState<Assessment[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([getExecTeams(), getMyHistory()])
      .then(([t, h]) => {
        setTeams(t.teams);
        setMine(h.assessments);
      })
      .catch((err: unknown) => {
        const m = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        setError(m ?? 'Failed to load exec view');
      });
  }, []);

  if (!teams || !mine) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        {error ? <Alert severity="error">{error}</Alert> : <CircularProgress />}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h4" component="h1">Executive View</Typography>
        <Typography variant="body2">
          All CLR teams ({user?.email}). Self-eval scores are excluded from team averages.
        </Typography>
      </Box>

      {error && <Alert severity="error" role="alert">{error}</Alert>}

      <Paper elevation={5}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label="TEAMS" sx={{ minWidth: 72, maxWidth: 264, height: 48, textTransform: 'uppercase' }} />
          <Tab label="MY HISTORY" sx={{ minWidth: 72, maxWidth: 264, height: 48, textTransform: 'uppercase' }} />
        </Tabs>
      </Paper>

      {tab === 0 && (() => {
        const totalSubmissions = teams.reduce((s, t) => s + t.assessments.length, 0);
        const handleExport = () => {
          if (totalSubmissions === 0) return;
          const rows = teams.flatMap((section) =>
            section.assessments.map((a) => ({
              team: section.teamName,
              email: a.userEmail ?? a.userId,
              period: a.cadencePeriod,
              submitted: new Date(a.submittedAt).toISOString(),
              totalScore: a.totalScore.toFixed(2),
              maturityLevel: a.maturityLevel,
              d1: (a.dimensionScores?.d1 ?? 0).toFixed(2),
              d2: (a.dimensionScores?.d2 ?? 0).toFixed(2),
              d3: (a.dimensionScores?.d3 ?? 0).toFixed(2),
              d4: (a.dimensionScores?.d4 ?? 0).toFixed(2),
              d5: (a.dimensionScores?.d5 ?? 0).toFixed(2),
              d6: (a.dimensionScores?.d6 ?? 0).toFixed(2),
            })),
          );
          exportToCsv(`ai-maturity-exec-all-teams-${todayIso()}.csv`, rows);
        };
        return (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                disabled={totalSubmissions === 0}
                onClick={handleExport}
              >
                {totalSubmissions === 0 ? 'No data' : 'Export CSV'}
              </Button>
            </Box>
            {teams.map((section) => {
          const n = section.assessments.length;
          const avg = n === 0 ? 0 : section.assessments.reduce((s, a) => s + a.totalScore, 0) / n;
          return (
            <Card key={section.teamId} elevation={3}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
                  <Typography variant="h5" component="h2">{section.teamName}</Typography>
                  <Typography variant="body2">
                    {n === 0
                      ? 'No submissions yet'
                      : `n=${n} · avg ${avg.toFixed(2)}`}
                  </Typography>
                </Box>
                {n === 0 ? (
                  <Typography variant="body2">No submissions this period</Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>User</TableCell>
                          <TableCell>Period</TableCell>
                          <TableCell>Submitted</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell align="right">Level</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {section.assessments.map((a) => (
                          <TableRow key={a.assessmentId}>
                            <TableCell>{a.userEmail ?? `${a.userId.slice(0, 8)}...`}</TableCell>
                            <TableCell>{a.cadencePeriod}</TableCell>
                            <TableCell>{new Date(a.submittedAt).toLocaleString()}</TableCell>
                            <TableCell align="right">{a.totalScore.toFixed(2)}</TableCell>
                            <TableCell align="right">L{a.maturityLevel}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          );
        })}
          </>
        );
      })()}

      {tab === 1 && (
        <Card elevation={3}>
          <CardContent>
            <Typography variant="h5" component="h2" gutterBottom>
              My History
            </Typography>
            {mine.length === 0 ? (
              <Typography variant="body2">You haven't taken an assessment yet.</Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Period</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Level</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mine.map((a) => (
                      <TableRow key={a.assessmentId}>
                        <TableCell>{a.cadencePeriod}</TableCell>
                        <TableCell>{new Date(a.submittedAt).toLocaleString()}</TableCell>
                        <TableCell align="right">{a.totalScore.toFixed(2)}</TableCell>
                        <TableCell align="right">L{a.maturityLevel}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
